import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api-client"

export type FlowSchedule = {
  id: string
  workflow_id: string
  owner: string
  owner_name?: string | null
  cron?: string | null
  interval_minutes?: number | null
  timezone: string
  enabled: boolean
  default_prompt: string
  default_inputs: Record<string, unknown>
  notify: { emails?: string[]; on?: string[] }
  blocked_reason?: string | null
  next_run?: string | null
  last_run_id?: string | null
  last_run_at?: string | null
  created_at: string
  updated_at: string
}

export type UpsertScheduleInput = {
  id?: string
  cron?: string | null
  interval_minutes?: number | null
  timezone?: string
  enabled?: boolean
  default_prompt?: string
  default_inputs?: Record<string, unknown>
  notify?: { emails?: string[]; on?: string[] }
}

const schedulesKey = (workflowId: string) => ["workflow-schedules", workflowId] as const

export function useWorkflowSchedules(workflowId: string | undefined) {
  return useQuery({
    queryKey: schedulesKey(workflowId ?? ""),
    enabled: Boolean(workflowId),
    queryFn: async (): Promise<FlowSchedule[]> => {
      const res = (await api.get(`/explorer/workflow/${workflowId}/schedules`)) as unknown as {
        schedules?: FlowSchedule[]
      }
      return res.schedules ?? []
    },
  })
}

export function useUpsertWorkflowSchedule(workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpsertScheduleInput): Promise<FlowSchedule> => {
      return (await api.post(
        `/explorer/workflow/${workflowId}/schedule`,
        input
      )) as unknown as FlowSchedule
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: schedulesKey(workflowId) })
    },
    onSuccess: (schedule) => {
      queryClient.setQueryData<FlowSchedule[]>(schedulesKey(workflowId), (existing = []) => {
        const index = existing.findIndex((item) => item.id === schedule.id)
        if (index === -1) return [schedule, ...existing]
        return existing.map((item) => (item.id === schedule.id ? schedule : item))
      })
    },
    onSettled: () => {
      // Keep this non-async: awaiting the refetch here would hold the mutation
      // in `isPending` until a second background GET resolves, leaving the
      // schedule Switch disabled and stale long after the upsert succeeded.
      void queryClient.invalidateQueries({ queryKey: schedulesKey(workflowId) })
    },
  })
}

export function useDeleteWorkflowSchedule(workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduleId: string): Promise<void> => {
      await api.delete(`/explorer/workflow/${workflowId}/schedule/${scheduleId}`)
    },
    onSuccess: (_result, scheduleId) => {
      queryClient.setQueryData<FlowSchedule[]>(schedulesKey(workflowId), (existing = []) =>
        existing.filter((item) => item.id !== scheduleId)
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: schedulesKey(workflowId) })
    },
  })
}
